import * as React from 'react';
import { DexSearchStore, OrderConverter } from '@zen-dex/utils';
import {
  Title,
  Box,
  useModal,
  Modal,
  ModalContent,
  Alert,
  notification,
} from '@zen-common/components-base';
import { WalletStore } from '@zen/common-app-parts';
import { assetUtils } from '@zen/common-utils';
import usePreventAction from '../../../../../../utils/usePreventAction';
import { PriceQuantityTotalMakeForm } from '../PriceQuantityTotalForm';
import { usePriceQuantityTotal } from '../PriceQuantityTotalForm/usePriceQuantityTotal';
import { useValidatePriceQuantityTotal } from '../modules/useValidatePriceQuantityTotal';
import { StyledContentPanel, StyledTab } from './MakeOrderForm.styled';
import MakeOrderModalProcess from './MakeOrderModalProcess';

const MakeOrderForm = () => {
  const { preventAction } = usePreventAction();
  const { hide, show } = useModal();
  const { state: searchState } = DexSearchStore.useStore();
  const { base, quote } = searchState;
  const [isBuy, setIsBuy] = React.useState(true);
  const {
    price,
    quantity,
    total,
    limitError,
    handlePriceChange,
    handleQuantityChange,
    handleTotalChange,
  } = usePriceQuantityTotal();
  const {
    state: { isLoggedIn, hasPassword, balances },
  } = WalletStore.useStore();

  const shouldDisplayLogin = hasPassword && !isLoggedIn;
  // round the amounts before validation. the contract accepts integers only
  const rounded = React.useMemo(() => {
    return OrderConverter.roundMakeAmounts({
      isBuyOrder: isBuy,
      quantity: quantity.safeValue,
      total: total.safeValue,
    });
  }, [isBuy, quantity.safeValue, total.safeValue]);
  const validate = useValidatePriceQuantityTotal({
    base,
    quote,
    isBuyAction: isBuy,
  });
  const validation = React.useMemo(
    () =>
      validate({
        original: {
          price: price.value,
          quantity: quantity.value,
          total: total.value,
        },
        rounded,
      }),
    [price.value, quantity.value, rounded, total.value, validate]
  );

  function handleCancel() {
    hide();
  }
  function handleError(error) {
    show(() => (
      <Alert type={Alert.TYPE.ERROR} onDismiss={hide} title="Error">
        {error.message}
      </Alert>
    ));
  }
  function handleSuccess() {
    price.display = '';
    price.value = '';
    validation.price = '';
    quantity.display = '';
    quantity.value = '';
    validation.quantity = '';
    total.display = '';
    total.value = '';
    validation.total = '';
    notification('Order created successfully', {
      type: notification.TYPE.SUCCESS,
    });
    hide();
  }

  const available = React.useMemo(() => {
    if (shouldDisplayLogin || !hasPassword) return;
    if (isBuy) {
      const available = balances.find((balance) => assetUtils.isEqual(balance.asset, quote));
      return available && { asset: quote, amount: available.amount };
    }
    const available = balances.find((balance) => assetUtils.isEqual(balance.asset, base));
    return available && { asset: base, amount: available.amount };
  }, [balances, base, hasPassword, isBuy, quote, shouldDisplayLogin]);

  const handleSubmit = async () => {
    if (preventAction()) return;

    show(() => (
      <Modal>
        <ModalContent>
          <MakeOrderModalProcess
            base={base}
            quote={quote}
            isBuy={isBuy}
            original={{ price: price.value, quantity: quantity.value, total: total.value }}
            rounded={rounded}
            onCancel={handleCancel}
            onError={handleError}
            onSuccess={handleSuccess}
          />
        </ModalContent>
      </Modal>
    ));
  };

  return (
    <>
      <Box display="flex">
        <StyledTab active={isBuy} onClick={() => setIsBuy(true)}>
          <Title mb="0" size="4">
            BUY
          </Title>
        </StyledTab>
        <StyledTab active={!isBuy} onClick={() => setIsBuy(false)}>
          <Title mb="0" size="4">
            SELL
          </Title>
        </StyledTab>
      </Box>
      <StyledContentPanel>
        <PriceQuantityTotalMakeForm
          submitText={`Make ${isBuy ? 'Buy' : 'Sell'} Order`}
          onSubmit={handleSubmit}
          onPriceChange={handlePriceChange}
          onQuantityChange={handleQuantityChange}
          onTotalChange={handleTotalChange}
          limitError={limitError}
          price={price}
          quantity={quantity}
          total={total}
          validation={validation}
          available={available}
          isBuy={isBuy}
        />
      </StyledContentPanel>
    </>
  );
};

export default MakeOrderForm;
